import { useEffect, useState } from "react";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { title: "", content: "", priority: "normal", target_role: "jamaah" };

export default function AdminPengumuman() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/pengumuman`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/admin/pengumuman`, form);
      toast.success("Pengumuman berhasil dikirim");
      setModalOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/pengumuman/${deleteId}`);
      toast.success("Pengumuman berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-pengumuman-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pengumuman</h1>
          <p className="text-sm text-[#64748B]">Kirim pengumuman kepada jamaah atau pembimbing.</p>
        </div>
        <Button data-testid="admin-pengumuman-add-button" onClick={() => setModalOpen(true)} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Kirim Pengumuman</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Megaphone} title="Belum ada pengumuman" description="Kirim pengumuman pertama Anda kepada jamaah." />
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-pengumuman-item-${p.id}`} className={`rounded-xl border p-5 ${p.priority === "important" ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-white"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-[#1F2937]">{p.title}</p>
                  <p className="mt-1 text-sm text-[#64748B]">{p.content}</p>
                  <p className="mt-2 text-xs text-[#94a3b8]">Untuk: {p.target_role} &middot; {new Date(p.created_at).toLocaleDateString("id-ID")}</p>
                </div>
                <Button variant="ghost" size="icon" data-testid={`admin-pengumuman-delete-${p.id}`} onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-pengumuman-modal">
          <DialogHeader><DialogTitle>Kirim Pengumuman</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul</Label><Input data-testid="announcement-form-title" value={form.title} onChange={(e) => update("title", e.target.value)} /></div>
            <div><Label>Isi Pengumuman</Label><Textarea data-testid="announcement-form-content" value={form.content} onChange={(e) => update("content", e.target.value)} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prioritas</Label>
                <Select value={form.priority} onValueChange={(v) => update("priority", v)}>
                  <SelectTrigger data-testid="announcement-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="important">Penting</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Target</Label>
                <Select value={form.target_role} onValueChange={(v) => update("target_role", v)}>
                  <SelectTrigger data-testid="announcement-form-target"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="jamaah">Jamaah</SelectItem><SelectItem value="pembimbing">Pembimbing</SelectItem><SelectItem value="all">Semua</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="admin-pengumuman-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Kirim</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Pengumuman?" onConfirm={handleDelete} testId="admin-pengumuman-delete-confirm" />
    </div>
  );
}
